import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../shared/ReportShell';
import { useCompanyName } from '../../shared/useCompanyName';
import { formatDate } from '../../shared/tenantClock';
import { formatQty } from './packageShared';
import { companyAddressLines, deliveryAddressLines, MetaRow } from './PickSlipReportView';

const cell = { padding: '6px 8px 6px 0', verticalAlign: 'top' };
const parcelHeading = { color: '#333', fontSize: 13, fontWeight: 700, padding: '14px 0 4px', borderBottom: '1.5px solid #333' };
const packageHeading = { color: '#333', fontSize: 12, fontWeight: 700, padding: '10px 0 3px 12px', borderBottom: '1px solid #999' };

const qtyWithUom = (qty, uom) => `${formatQty(qty)}${uom ? ` ${uom}` : ''}`;

// "1 PCK Backordered", one per kind (a line can be both).
function NotSupplied({ line }) {
  return (line.not_supplied || []).map((n) => (
    <div key={n.kind} style={{ whiteSpace: 'nowrap' }}>
      {qtyWithUom(n.qty, line.uom)} <strong style={{ fontWeight: 700 }}>{n.label}</strong>
    </div>
  ));
}

function LineRow({ line, showQty = true, indent = false }) {
  return (
    <tr style={{ borderBottom: '1px solid #ddd' }}>
      <td style={{ ...cell, overflowWrap: 'break-word', paddingLeft: indent ? 12 : 0 }}>{line.sku}</td>
      <td style={cell}>{line.name}</td>
      <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>{showQty ? qtyWithUom(line.qty, line.uom) : ''}</td>
      <td style={{ ...cell, paddingRight: 0, textAlign: 'right' }}><NotSupplied line={line} /></td>
    </tr>
  );
}

// The Packing List (issues #296, #445, vis_order_fulfilment.md "Packing
// List"): one per Shipment, enclosed in Parcel 1, for a receiver who wants
// the contents on paper. Each Parcel is headed "Parcel N of M" with its own
// goods, then each Package inside it under "Parcel N · Package N of M", so
// every label matches a section; then the order's lines still on backorder
// or cancelled that aren't in this shipment. No prices, GST, comments or
// Hold (the Pick Slip, PickSlipReportView.js, is the warehouse's own copy).
// Opened with `shipmentId` to reprint a shipment's, or `orderType` and
// `orderId` at Pack for the packages not yet shipped. Who it speaks for
// comes from the order type, same as the Pick Slip.
function PackingListReportView({ shipmentId, orderType, orderId, onClose }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companySettings, setCompanySettings] = useState(null);
  const { logoUrl } = useCompanyName();

  useEffect(() => {
    setLoading(true);
    const params = shipmentId ? { shipment_id: shipmentId } : { order_type: orderType, order_id: orderId };
    axios.get('/api/fulfilment/packing-list', { params })
      .then((res) => setList(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load the Packing List'))
      .finally(() => setLoading(false));
  }, [shipmentId, orderType, orderId]);

  useEffect(() => {
    axios.get('/api/company-settings')
      .then((res) => setCompanySettings(res.data.data || null))
      .catch(() => setCompanySettings(null));
  }, []);

  const sender = list?.sender || (companySettings ? { name: companySettings.company_name, address_lines: companyAddressLines(companySettings) } : null);
  const showLogo = !list?.sender && logoUrl;
  const deliverTo = list?.deliver_to || {};
  const sections = list?.sections || [];
  const notInShipment = list?.not_in_shipment || [];
  const anyLines = sections.some((s) => s.lines.length > 0);

  return (
    <ReportShell
      onClose={onClose}
      printDisabled={loading || !list}
      hideRunningHeader
      hideConfidentialityNote
      hideLogo
      hideCompanyNameHeader
      beforeTitle={list && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ width: 180 }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Packing List</div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{list.type_label} No.</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{list.display_number}</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            {showLogo && <img src={logoUrl} alt="" style={{ maxHeight: 56, maxWidth: 260, objectFit: 'contain' }} />}
          </div>
          <div style={{ width: 180, textAlign: 'right' }}>
            {list.parcel_count > 0 && (
              <div style={{ fontSize: 18, fontWeight: 700 }}>{list.parcel_count} Parcel{list.parcel_count === 1 ? '' : 's'}</div>
            )}
          </div>
        </div>
      )}
      footerNote={list ? formatReportTimestamp() : undefined}
    >
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {list && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginBottom: 4, fontSize: 13 }}>
            <div style={{ maxWidth: 240 }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>SUPPLIED BY</div>
              <div style={{ fontWeight: 600 }}>{sender?.name || '—'}</div>
              {(sender?.address_lines || []).map((l) => <div key={l}>{l}</div>)}
            </div>

            <div style={{ maxWidth: 240 }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>DELIVER TO</div>
              <div style={{ fontWeight: 600 }}>{deliverTo.name || '—'}</div>
              {deliverTo.attention && <div>Attn: {deliverTo.attention}</div>}
              {deliveryAddressLines(deliverTo.address).map((l) => <div key={l}>{l}</div>)}
            </div>

            <div style={{ minWidth: 180 }}>
              <MetaRow label="Order Date" value={formatDate(list.order_date)} />
              {list.reference && <MetaRow label={list.reference.label} value={list.reference.value} />}
              {list.shipped_date && <MetaRow label="Shipped" value={formatDate(list.shipped_date)} />}
              {list.carrier && <MetaRow label="Carrier" value={list.carrier.name} />}
              {list.tracking_number && <MetaRow label="Tracking #" value={list.tracking_number} />}
            </div>
          </div>

          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize, marginTop: 8 }}>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '46%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '22%' }} />
            </colgroup>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                <th style={cell}>SKU</th>
                <th style={cell}>Description</th>
                <th style={{ ...cell, textAlign: 'right' }}>Qty</th>
                <th style={{ ...cell, paddingRight: 0, textAlign: 'right' }}>Not Supplied</th>
              </tr>
            </thead>
            {sections.map((section, i) => (
              <tbody key={section.heading || i} className="page-break-avoid">
                {section.heading && (
                  <tr><td colSpan={4} style={section.kind === 'package' ? packageHeading : parcelHeading}>{section.heading}</td></tr>
                )}
                {section.lines.map((line, j) => <LineRow key={`${line.line_id}-${j}`} line={line} indent={section.kind === 'package'} />)}
              </tbody>
            ))}
            {!anyLines && (
              // A shipment from before shipments recorded their items has
              // nothing to list.
              <tbody><tr><td colSpan={4} style={{ ...cell, color: '#888' }}>{list.shipped_date ? 'No items recorded for this shipment.' : 'Nothing packed yet.'}</td></tr></tbody>
            )}
            {notInShipment.length > 0 && (
              <tbody className="page-break-avoid">
                <tr><td colSpan={4} style={parcelHeading}>Not in this shipment</td></tr>
                {notInShipment.map((line) => <LineRow key={line.line_id} line={line} showQty={false} />)}
              </tbody>
            )}
          </table>
        </>
      )}
    </ReportShell>
  );
}

export default PackingListReportView;
